import { FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useRentalCarApi } from "../../http/rentalCars/api";
import { RentalCarNoCarDepency } from "../../http/rentalCars/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { downloadFileFromData, formatDateNumeric } from "../../types/utils";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import CustomLink from "../ui/CustomLink";
import ExportButton from "../ui/ExportButton";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction } from "../ui/ListItemMenu";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CreateRentalCarDialog from "./CreateRentalCarDialog";
import DeleteRentalCarDialog from "./DeleteRentalCarDialog";
import EditRentalCarDialog from "./EditRentalCarDialog";

const RENTAL_CAR_FIELDS = [
  "id",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "startDate",
  "endDate",
  "driver.id",
  "driver.email",
  "driver.fullName",
  "plateNumber",
  "leasingCompany.id",
  "leasingCompany.name",
  "manufacturer.id",
  "manufacturer.name",
  "model",
] as const;
type RentalCarsField = (typeof RENTAL_CAR_FIELDS)[number];
type RentalCarsSearch = NestedPick<RentalCarNoCarDepency, RentalCarsField>;

type RentalCarsProps = {
  onUpdate?: () => void;
};

const RentalCars: FC<RentalCarsProps> = ({ onUpdate }: RentalCarsProps) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const filters = useListFilters({ isSearchable: true, prefix: "cd" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());

  const [itemToDelete, setItemToDelete] = useState<RentalCarsSearch>();
  const [itemToEdit, setItemToEdit] = useState<RentalCarsSearch>();
  const [showNewrental, setShowNewRental] = useState<boolean>();

  const rentalCarsApi = useRentalCarApi();
  const { id: carId } = useParams<{ id: string }>();

  const query: PaginationQueryWithFields<RentalCarsSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: RENTAL_CAR_FIELDS,
  };

  const result = rentalCarsApi.useAdminRentalCar(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: rentalCarsApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: RentalCarsSearch): Action[] => {
    const actions: Action[] = [];

    userSettings.permissions.rentalCars.read && actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    userSettings.permissions.rentalCars.delete &&
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));

    return actions;
  };

  const content = (
    <>
      <Table<RentalCarsSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: userSettings.permissions.rentalCars.insert ? (
            <Button glyph={SvgAdd} buttonProps={{ onClick: () => setShowNewRental(true) }} />
          ) : undefined,
          secondary: (
            <ExportButton.Excel
              onClick={async () => {
                const r = await rentalCarsApi.exportExcel(query, aborter.current.signal);
                r && downloadFileFromData(r?.data, "Rental cars", r?.contentType);
              }}
            />
          ),
        }}
        head={
          <>
            <Table.Th
              title={i18n.t.plateNumbers.singular}
              fieldName="rentalCar.plateNumber"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.leasingCompanies.singular}
              fieldName="rentalCar.leasingCompany.name"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.manufacturers.singular}
              fieldName="rentalCar.manufacturer.name"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th title={i18n.t.common.model} fieldName="rentalCar.model" order={orderOptions} />

            <Table.Th
              title={i18n.t.drivers.singular}
              fieldName="rentalCar.driver.fullName"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />

            <Table.Th title={i18n.t.common.startDate} fieldName="rentalCar.startDate" order={orderOptions} />
            <Table.Th title={i18n.t.common.endDate} fieldName="rentalCar.endDate" order={orderOptions} />
            {!carId && (
              <>
                <Table.Th
                  title={i18n.t.cars.singular}
                  fieldName="rentalCar.car.plateNumber"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
                <Table.Th
                  title={i18n.t.common.carVin}
                  fieldName="rentalCar.car.vin"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
              </>
            )}
            <th></th>
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          setItemToEdit(item);
        }}
        renderRow={(item) => (
          <>
            <td>{item.plateNumber}</td>
            <td>{item.leasingCompany.name}</td>
            <td>{item.manufacturer?.name}</td>
            <td>{item.model}</td>
            <td>
              {!!userSettings.permissions.users.read ? (
                <CustomLink to={`/admin/users/${item.driver.id}`}>{item.driver.fullName}</CustomLink>
              ) : (
                item.driver.fullName
              )}
            </td>
            <td>{!!item.startDate && formatDateNumeric(item.startDate, i18n.language)}</td>
            <td>{!!item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
            {!carId && (
              <>
                <td>
                  <CustomLink to={`/admin/cars/${item.car.id}`}>
                    {item.car.currentPlateNumber || item.car.lastPlateNumber}
                  </CustomLink>
                </td>
                <td>
                  <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.vehicleIdentificationNumber}</CustomLink>
                </td>
              </>
            )}
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!itemToDelete && (
        <DeleteRentalCarDialog
          rentalCarId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={(success) => {
            setItemToDelete(undefined);
            success && onUpdate?.();
          }}
        />
      )}

      {!!itemToEdit && (
        <EditRentalCarDialog
          rentalCarId={itemToEdit.id}
          onClose={(success) => {
            setItemToEdit(undefined);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}

      {!!showNewrental && (
        <CreateRentalCarDialog
          onClose={(success) => {
            setShowNewRental(false);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.rentalCars.plural} />
      <Panel>{content}</Panel>
    </>
  );
};

export default RentalCars;
